import { supabase } from '../../lib/supabase';
import type { User } from '../../types/auth';

export const supabaseAuthService = {
  getCurrentUser: async () => {
    try {
      console.log('Getting current user...'); // Debug log
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('Error getting auth user:', userError);
        throw userError;
      }

      if (!user) {
        console.log('No authenticated user found');
        return null;
      }

      console.log('Found auth user:', user.id);

      // Fetch user profile with better error handling
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        throw new Error('Failed to fetch user profile');
      }

      if (!profile) {
        console.error('No profile found for user:', user.id);
        throw new Error('User profile not found');
      }

      console.log('Found user profile:', profile);

      return {
        id: user.id,
        email: user.email!,
        firstName: profile.first_name,
        lastName: profile.last_name,
        role: profile.role,
        isActive: profile.is_active
      };
    } catch (error) {
      console.error('Error getting current user:', error);
      throw error;
    }
  },

  login: async ({ email, password }: { email: string; password: string }) => {
    try {
      console.log('Attempting login for:', email);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error('Auth error:', error);
        throw error;
      }

      if (!data.user) {
        console.error('No user returned from auth');
        throw new Error('No user returned from auth');
      }

      console.log('Auth successful, fetching profile...');

      // Fetch user profile with better error handling
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', data.user.id)
        .single();

      if (profileError) {
        console.error('Profile fetch error:', profileError);
        throw new Error('Failed to fetch user profile');
      }

      if (!profile) {
        console.error('No profile found for user:', data.user.id);
        throw new Error('User profile not found');
      }

      if (!profile.is_active) {
        throw new Error('Account is inactive');
      }

      console.log('Login successful, returning user data');

      return {
          id: data.user.id,
          email: data.user.email!,
          firstName: profile.first_name,
          lastName: profile.last_name,
          role: profile.role,
          isActive: profile.is_active
      };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
},

  logout: async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }
};
